import { Middleware, MiddlewareAPI } from "redux"

import { isSentryActive, logBreadcrumb } from "../analytics"
import { parseApiError } from "../api/apiUtils"
import { isNetworkError } from "../api/utils"
import { HTTP_STATUS } from "../constants"
import { detectLockedFeature } from "../fetch"
import { setIsOffline } from "./app/appSlice"
import { clearAuth } from "./auth/authSlice"
import { setHasPaid } from "./payment/paymentSlice"
import { isFulfilled, isRejected, isRejectedWithValue } from "@reduxjs/toolkit"
import { captureException, captureMessage } from "@sentry/react"

type ParsedReduxError = {
  message?: string
}

// Those errors are expected and we are using them to set app state
const INFO_ERRORS = [
  "Authentication credentials were not provided.",
  "Payment required for functionalities to work.",
]

const mappedError = (error: string): string => {
  const normalizedError = error.toLowerCase()
  if (normalizedError.includes("not found")) {
    return "Requested resource was not found."
  }

  return error
}

export const sentryMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    try {
      if (!isSentryActive || isFulfilled(action)) {
        return next(action)
      }

      if (isRejectedWithValue(action) || isRejected(action)) {
        // do not report 401, 402, 403 errors to Sentry
        if (
          typeof action.payload === "object" &&
          action.payload !== null &&
          "status" in action.payload &&
          typeof action.payload.status === "number" &&
          (
            [
              HTTP_STATUS.UNAUTHORIZED,
              HTTP_STATUS.PAYMENT_REQUIRED,
              HTTP_STATUS.FORBIDDEN,
            ] as number[]
          ).includes(action.payload.status)
        ) {
          return next(action)
        }
      }

      if (isRejectedWithValue(action)) {
        const apiError = parseApiError(action.payload) as ParsedReduxError

        if (!apiError) {
          return next(action)
        }

        logBreadcrumb("Redux action failed (middleware rejected with value)")

        if (apiError?.message) {
          logBreadcrumb(`Error: ${apiError.message}`)
          captureMessage(mappedError(apiError.message))
          return next(action)
        }

        if (typeof action.payload === "string") {
          logBreadcrumb(`Error: ${action.payload}`)
          captureMessage(mappedError(action.payload))
          return next(action)
        }

        if (typeof action.payload === "object" && action.payload !== null) {
          const payloadObj = action.payload as Record<string, unknown>
          for (const key in payloadObj) {
            const value = payloadObj[key]
            logBreadcrumb(`Error: ${key} - ${value}`)
          }

          if (Object.keys(payloadObj).length === 0) {
            apiError.message = "An unknown error occurred"
          } else {
            apiError.message = Object.values(payloadObj)[0] as string
          }
        } else {
          apiError.message = "An unknown error occurred"
        }

        captureMessage(apiError.message)

        return next(action)
      }

      if (isRejected(action)) {
        const { error, meta, type } = action

        /*
         * RTK-query rejects with a ConditionError when the query is set to be skipped
         */
        if ("name" in error && error.name === "ConditionError") {
          return next(action)
        }

        /*
         * we are fetching employee screening data every time the user check-ins
         * to check if the user has a screening the endpoint returns 404 if the user
         * doesn't have a screening so we are ignoring this error
         */
        if (
          type === "api/executeQuery/rejected" &&
          (meta?.arg as { endpointName?: string })?.endpointName ===
            "fetchEmployeeScreening"
        ) {
          return next(action)
        }

        logBreadcrumb("Redux action failed (middleware rejected)")

        /*
         * we are using those errors to set app state
         */
        if (INFO_ERRORS.includes(error.message ?? "")) {
          captureMessage(mappedError(error.message ?? ""), "info")
          return next(action)
        }

        if (error.message) {
          logBreadcrumb(`Error: ${error.message}`)
          captureMessage(mappedError(error.message))
          captureException(error)
        }

        return next(action)
      }

      return next(action)
    } catch (error) {
      console.error("Sentry middleware error", error)
      return next(action)
    }
  }

/**
 * JOAN-10552: surfaces a global "no connection" overlay when API calls fail
 * because the device is offline. RTK Query / thunk failures carry the network
 * signal (`FETCH_ERROR` / "Failed to fetch"), which `isNetworkError` detects.
 * The flag is cleared as soon as any request succeeds again, so the overlay
 * disappears once connectivity returns.
 */
export const offlineMiddleware: Middleware =
  ({ dispatch, getState }: MiddlewareAPI) =>
  (next) =>
  (action) => {
    try {
      const isOffline = (getState() as { app: { isOffline: boolean } }).app
        .isOffline

      if (isFulfilled(action)) {
        if (isOffline) {
          dispatch(setIsOffline(false))
        }

        return next(action)
      }

      if (!isOffline) {
        if (isRejectedWithValue(action) && isNetworkError(action.payload)) {
          dispatch(setIsOffline(true))
        } else if (isRejected(action) && isNetworkError(action.error)) {
          dispatch(setIsOffline(true))
        }
      }

      return next(action)
    } catch (error) {
      console.error("Offline middleware error", error)
      return next(action)
    }
  }

export const apiErrorHandlingMiddleware: Middleware =
  ({ dispatch }: MiddlewareAPI) =>
  (next) =>
  (action) => {
    try {
      if (!isRejectedWithValue(action) && !isRejected(action)) {
        return next(action)
      }

      // Handle isRejected actions (e.g., from RTK Query)
      if (isRejected(action)) {
        // Check if it's a 401 error even in rejected actions
        const error = action.error as any
        const meta = action.meta as any
        const payload = action.payload as any

        // RTK Query can store status in multiple places depending on how the error was thrown
        const status =
          meta?.baseQueryMeta?.response?.status ||
          error?.status ||
          payload?.status

        if (status === HTTP_STATUS.UNAUTHORIZED) {
          dispatch(clearAuth())
        }

        // 402 marks the account unpaid; detectLockedFeature only refines which feature to lock
        if (status === HTTP_STATUS.PAYMENT_REQUIRED) {
          dispatch(
            setHasPaid(detectLockedFeature((payload as { url: string }).url)),
          )
        }

        return next(action)
      }

      // Handle isRejectedWithValue actions (e.g., from old thunks)
      const { payload } = action as any
      const apiError = parseApiError(payload) as ParsedReduxError

      if (!apiError) {
        return next(action)
      }

      const { status } = payload

      if (status === HTTP_STATUS.UNAUTHORIZED) {
        dispatch(clearAuth())
        return next(action)
      }

      if (status === HTTP_STATUS.PAYMENT_REQUIRED) {
        dispatch(
          setHasPaid(detectLockedFeature((payload as { url: string }).url)),
        )
        return next(action)
      }

      return next(action)
    } catch (error) {
      console.error("API error handling middleware error", error)
      return next(action)
    }
  }
